/**
 * AI chat streaming client.
 *
 * Unlike the rest of the ClaimFlow API surface this does NOT go through the
 * shared axios client — the backend replies with an SSE stream
 * (`text/event-stream`) which axios cannot incrementally surface. We use the
 * native `fetch` + `ReadableStream` reader so deltas render as they arrive.
 *
 * Wire contract:
 *   POST {VITE_ORIGAMI_API_URL}/api/claimflow/ai/chat
 *   body: { id_sin, ses, messages: [{ role, content }] }
 *   resp: blank-line separated SSE frames, each `data: <payload>`, where
 *         payload is `{"delta":"<chunk>"}`, `{"error":"..."}`, or `[DONE]`.
 */

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface StreamChatParams {
  ses: string | number;
  idSin: string | number;
  messages: ChatMessage[];
  onDelta: (text: string) => void;
  signal?: AbortSignal;
}

const AUTH_TOKEN_KEY = 'claimflow_auth_token';
const ACTIVE_TENANT_KEY = 'claimflow_active_tenant_id';

export async function streamChat({
  ses,
  idSin,
  messages,
  onDelta,
  signal,
}: StreamChatParams): Promise<void> {
  const baseUrl = import.meta.env.VITE_ORIGAMI_API_URL as string;
  const url = `${baseUrl}/api/claimflow/ai/chat`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem(ACTIVE_TENANT_KEY);
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ id_sin: idSin, ses, messages }),
    signal,
  });

  if (!response.ok) {
    throw new Error(
      `AI chat request failed with status ${String(response.status)}`,
    );
  }

  if (!response.body) {
    throw new Error('AI chat response had no body to stream.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line. Keep the trailing partial
    // frame in the buffer until its terminator arrives in a later read.
    let separatorIndex = buffer.indexOf('\n\n');
    while (separatorIndex !== -1) {
      const frame = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);

      if (handleFrame(frame, onDelta)) {
        return;
      }

      separatorIndex = buffer.indexOf('\n\n');
    }
  }

  // Flush any trailing frame that arrived without a final blank line.
  const tail = buffer.trim();
  if (tail) {
    handleFrame(tail, onDelta);
  }
}

/**
 * Parses a single SSE frame. Returns `true` when the `[DONE]` sentinel was
 * seen so the caller can stop reading. Throws when the payload carries an
 * `.error`.
 */
function handleFrame(frame: string, onDelta: (text: string) => void): boolean {
  const lines = frame.split('\n');
  for (const line of lines) {
    if (!line.startsWith('data:')) continue;
    const payload = line.slice(5).trim();
    if (!payload) continue;
    if (payload === '[DONE]') {
      return true;
    }

    const parsed = JSON.parse(payload) as { delta?: string; error?: string };
    if (parsed.error) {
      throw new Error(parsed.error);
    }
    if (typeof parsed.delta === 'string') {
      onDelta(parsed.delta);
    }
  }
  return false;
}
